import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useConfirmShowcaseCheckout, useShowcase, useShowcaseCheckout, useShowcasePortal } from "@/hooks/useShowcase";
import { formatPlanPrice, usePackage } from "@/hooks/usePlans";
import { AGENT_SHOWCASE } from "@/services/showcaseService";
import "./ShowcaseBilling.scss";

export const priceLineOf = (pkg, forAgent = false) => {
  const amount = formatPlanPrice(forAgent ? pkg?.price?.monthly : pkg?.price?.website);
  return amount ? `${amount} a month` : null;
};

const dateOf = (value) =>
  value ? new Date(value).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

export const billingStateOf = (showcase, now = Date.now()) => {
  if (!showcase) return "loading";
  if (showcase.comped) return "comped";
  const status = showcase.billing?.status || showcase.status;
  if (status === "paused") return "paused";
  if (status === "active") return "active";
  if (status === "past_due") {
    const grace = showcase.billing?.graceUntil;
    return grace && new Date(grace).getTime() > now ? "grace" : "lapsed";
  }
  if (status === "canceled") return "canceled";
  return "none";
};

const doneLineOf = (checklist) => {
  if (checklist?.live) return "You\u2019re subscribed. Your Buyer Want\u2011Listings are live on your\u00a0website.";
  if (checklist?.siteAdded) return "You\u2019re subscribed. Next, put the code on your site so the Buyer Want\u2011Listings can\u00a0load.";
  return "You\u2019re subscribed. Next, add your website address so the Buyer Want\u2011Listings can load\u00a0there.";
};

const go = (url) => window.location.assign(url);

const ShowcaseBilling = ({ brokerageId, checkout, onCheckoutHandled, onGo }) => {
  const forAgent = brokerageId === AGENT_SHOWCASE;
  const query = useShowcase(brokerageId);
  const priceLine = priceLineOf(usePackage(forAgent ? "agentFeed" : "brokerage").pkg, forAgent);
  const startCheckout = useShowcaseCheckout(brokerageId);
  const portal = useShowcasePortal(brokerageId);
  const confirm = useConfirmShowcaseCheckout(brokerageId);
  const [result, setResult] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [licenseError, setLicenseError] = useState("");
  // isPending clears before the browser navigates; keep the button locked so a second click cannot open another session.
  const [redirecting, setRedirecting] = useState(false);
  const confirmedFor = useRef(null);

  useEffect(() => {
    if (!checkout) return;
    if (checkout.outcome === "canceled") {
      setResult("canceled");
      onCheckoutHandled?.();
      return;
    }
    if (!checkout.sessionId || confirmedFor.current === checkout.sessionId) return;
    confirmedFor.current = checkout.sessionId;
    setResult("confirming");
    confirm.mutate(checkout.sessionId, {
      onSuccess: () => setResult("done"),
      onError: () => setResult("failed"),
      onSettled: () => onCheckoutHandled?.(),
    });
  }, [checkout?.sessionId, checkout?.outcome]);

  const showcase = query.data?.showcase;
  if (!showcase) {
    return query.isError ? (
      <div className="showcase-billing__state">
        Billing did not load.{" "}
        <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => query.refetch()}>
          Try again
        </button>
      </div>
    ) : (
      <div className="showcase-billing__state" aria-busy="true">
        Loading billing…
      </div>
    );
  }

  const billing = showcase.billing || {};
  const state = billingStateOf(showcase);
  const canManage = billing.canManageBilling !== false;

  const license = showcase.license || null;
  const subscribe = () => {
    if (redirecting) return;
    if (license && !agreed) {
      setLicenseError("Read and accept the license terms to subscribe.");
      return;
    }
    startCheckout.mutate(license ? { acceptLicense: true, licenseVersion: license.version } : {}, {
      onSuccess: (data) => {
        if (!data?.url) return toast.error("Checkout did not open. Please try again.");
        setRedirecting(true);
        return go(data.url);
      },
      onError: (error) => toast.error(error.message),
    });
  };

  const manage = () =>
    portal.mutate(undefined, {
      onSuccess: (data) => (data?.url ? go(data.url) : toast.error("Billing did not open. Please try again.")),
      onError: (error) => toast.error(error.message),
    });

  const manageLink = canManage ? (
    <button type="button" className="showcase-billing__link" disabled={portal.isPending} onClick={manage}>
      {portal.isPending ? "Opening…" : "Manage billing"}
    </button>
  ) : (
    <span>{billing.payerName ? `${billing.payerName} manages billing.` : "Another manager handles billing."}</span>
  );

  const planLine =
    state === "comped"
      ? "Included by Gitcha, at no charge to your office"
      : state === "active"
        ? [
            priceLine,
            billing.currentPeriodEnd ? `renews ${dateOf(billing.currentPeriodEnd)}` : "renews monthly",
            billing.payerName ? `billed to ${billing.payerName}` : "",
          ]
            .filter(Boolean)
            .join(" · ")
        : null;

  const statusLine =
    state === "grace"
      ? `Your last payment did not go through. Your Buyer Want-Listings stay on your site until ${dateOf(billing.graceUntil)}; update your card before then.`
      : state === "lapsed"
        ? "Your Buyer Want-Listings are hidden on your site until the payment goes through. Your order and pins are kept."
        : state === "paused"
          ? "Gitcha paused your Buyer Want-Listings on your website. They are hidden there and you are not being charged."
          : state === "canceled"
            ? `Your subscription ended${billing.currentPeriodEnd ? ` on ${dateOf(billing.currentPeriodEnd)}` : ""}. Subscribe again and it comes back with your order and pins.`
            : null;

  return (
    <section className="content-block showcase-billing">
      <div className="title-sec">
        <h4>Buyer Want&#8209;Listings on your&nbsp;website</h4>
      </div>

      {result === "confirming" ? (
        <p className="showcase-billing__notice" role="status">
          Confirming your subscription with&nbsp;Stripe…
        </p>
      ) : null}
      {result === "done" ? (
        <p className="showcase-billing__notice is-good" role="status">
          {doneLineOf(showcase?.checklist)}{" "}
          {showcase?.checklist?.live ? null : (
            <button type="button" className="showcase-billing__link" onClick={() => onGo("setup")}>
              Finish setup
            </button>
          )}
        </p>
      ) : null}
      {result === "failed" ? (
        <p className="showcase-billing__notice is-bad" role="alert">
          We could not confirm your payment yet. If Stripe charged your card, it shows here within a few minutes.{" "}
          <button
            type="button"
            className="showcase-billing__link"
            onClick={() => {
              setResult("confirming");
              confirm.mutate(confirmedFor.current, {
                onSuccess: () => setResult("done"),
                onError: () => setResult("failed"),
              });
            }}
          >
            Check again
          </button>
        </p>
      ) : null}
      {result === "canceled" ? (
        <p className="showcase-billing__notice" role="status">
          Checkout closed. Nothing was&nbsp;charged.
        </p>
      ) : null}

      {planLine ? (
        <div className="showcase-billing__row">
          <span className="showcase-billing__label visually-hidden">Plan </span>
          <span className="showcase-billing__value">
            {planLine}
            {state === "active" ? manageLink : null}
          </span>
        </div>
      ) : statusLine ? (
        <div className="showcase-billing__row">
          <span className="showcase-billing__label visually-hidden">Status </span>
          <span className="showcase-billing__value">
            {statusLine}
            {state === "paused" ? (
              <a href="mailto:support@gitcha.com">Email support</a>
            ) : state === "canceled" ? null : (
              manageLink
            )}
          </span>
        </div>
      ) : null}

      {state === "none" || state === "canceled" ? (
        <div className="showcase-billing__offer">
          <p>
            {forAgent
              ? "Show your public Buyer Want\u2011Listings on your own website. Cancel any\u00a0time."
              : "Show your agents\u2019 public Buyer Want\u2011Listings on your website. Cancel any\u00a0time."}
          </p>
          {license ? (
            <div className="showcase-billing__license">
              <label>
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(event) => {
                    setAgreed(event.target.checked);
                    setLicenseError("");
                  }}
                />
                <span>
                  I accept the license terms for showing Buyer Want&#8209;Listings on {forAgent ? "my" : "our"}&nbsp;website.
                </span>
              </label>
              <button type="button" className="showcase-billing__link" aria-expanded={showTerms} onClick={() => setShowTerms(!showTerms)}>
                {showTerms ? "Hide the terms" : "Read the terms"}
              </button>
              {showTerms ? (
                <div className="showcase-billing__terms">
                  <h5>{license.title}</h5>
                  {license.sections.map((section) => (
                    <div key={section.heading}>
                      <strong>{section.heading}</strong>
                      <p>{section.body}</p>
                    </div>
                  ))}
                  <p className="showcase-billing__version">Version {license.version}</p>
                </div>
              ) : null}
              {licenseError ? (
                <p className="showcase-billing__error" role="alert">
                  {licenseError}
                </p>
              ) : null}
            </div>
          ) : null}
          <button
            type="button"
            className="btn btn-primary showcase-billing__button"
            disabled={startCheckout.isPending || redirecting || !priceLine}
            onClick={subscribe}
          >
            {startCheckout.isPending || redirecting ? (
              "Opening checkout…"
            ) : priceLine ? (
              `Subscribe for ${priceLine}`
            ) : (
              <span className="showcase-billing__price-loading" aria-busy="true" />
            )}
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default ShowcaseBilling;
